import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { ConfirmActionV2DialogResult } from '../../components/v2-layout/shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import type { PrefillNoticeAnswer } from '../../components/app-layout/shared/prefill-notice-dialog/prefill-notice-dialog.component';
import { prefillNoticeLabels } from '../../components/v2-layout/stripe-treasury/stripe-treasury.constants';
import { StripeConnectService } from './stripe-connect.service';
import { PrefillNoticeDialogStateService } from './prefill-notice-dialog-state.service';

/**
 * SD-9498: the pre-verification notice, shared by both KYC seams.
 *
 * The v2 dashboard and the v1 `FinancialConnectionsService` ask the same question at the same point
 * in the flow and act on the same three-way answer; only their terminal actions and teardown
 * operator differ. Keeping the preflight, the dialog configuration and the result semantics here
 * means a change to any of them lands once — the first version duplicated all three across the two
 * seams, and duplicated two defects along with them.
 */
@Injectable({
    providedIn: 'root',
})
export class PrefillNoticeService {
    constructor(
        private _stripeConnectService: StripeConnectService,
        private _dialog: PrefillNoticeDialogStateService,
    ) {}

    /**
     * Ask what PayHOA cannot send, and show the admin a notice when there is something to say.
     *
     * Emits `{success: true}` — proceed — when the org's details are all usable, and also whenever
     * anything about the check goes wrong. That fall-through is the point: this is an advisory, and
     * an advisory that blocks verification is worse than the bug it softens. A failed request, a
     * 200 whose shape we did not expect, a throw anywhere in the pipe — all resolve to "carry on",
     * so the caller's terminal path always runs and the caller's in-flight flag always clears.
     *
     * The answer keeps the shape both seams already read: `secondary` for "Review organization
     * settings" (with `success`, so each seam checks it first), `success` for "Continue to
     * verification", and `{success: false}` for Cancel, the X, Escape or a history back/forward.
     */
    public confirm(organizationId: number): Observable<ConfirmActionV2DialogResult> {
        return this._stripeConnectService.getPrefillCheck(organizationId).pipe(
            // `?? []` rather than a bare read: a 200 without the key is a real deploy-window shape
            // in this codebase, and dereferencing it would throw inside the pipe.
            map((response) => prefillNoticeLabels(response?.omissions ?? [])),
            switchMap((labels) => {
                if (!labels.length) {
                    return of<ConfirmActionV2DialogResult>({ success: true });
                }

                return this._dialog.ask({ labels }).pipe(map(toConfirmResult));
            }),
            // Last, deliberately: placed before switchMap it would only cover a failed request,
            // leaving a throw inside the pipe to reach an error-less subscribe — which on the v1
            // seam never calls onComplete(), latching the caller's button dead until a reload.
            catchError(() => of<ConfirmActionV2DialogResult>({ success: true })),
        );
    }
}

function toConfirmResult(answer: PrefillNoticeAnswer | null): ConfirmActionV2DialogResult {
    switch (answer) {
        case 'review-settings':
            return { success: true, secondary: true };
        case 'continue':
            return { success: true };
        default:
            return { success: false };
    }
}
